import { db, type Item, type Section } from './schema';

/**
 * What one screen of a room looks like: the sections and items sitting
 * directly in a container, plus the numbers that only a walk of the whole
 * tree can answer.
 *
 * The whole room is read in two queries and everything else is derived in
 * memory. A room is a few hundred rows of small records — no blobs, they're
 * referenced by id — and recursive counts against the database would be one
 * query per section per render.
 */

/** Where you are: a room, and possibly a section inside it. */
export type Container = {
  roomId: string;
  sectionId: string | undefined;
};

export type BoardEntry =
  | { kind: 'item'; id: string; sort: number; item: Item }
  | {
      kind: 'section';
      id: string;
      sort: number;
      section: Section;
      /** Items at any depth inside. Sections themselves don't count. */
      count: number;
      /** Up to four cover images, newest first. Empty if it holds nothing. */
      covers: string[];
    };

export type BoardView = {
  entries: BoardEntry[];
  /**
   * Every item in the room, at any depth. What a search within a board
   * filters: looking only inside the container you happen to be standing in
   * would answer a question nobody asked.
   */
  roomItems: Item[];
  /** Items at any depth in this container. */
  count: number;
  /** Items at any depth in the room, whatever container you're looking at. */
  roomCount: number;
  /** Outermost first, ending with the section you're in. Empty at the root. */
  path: Section[];
  /** Undefined when the section id in the URL doesn't exist. */
  section: Section | undefined;
};

const COVERS = 4;

export async function loadBoard(
  roomId: string,
  sectionId: string | undefined,
): Promise<BoardView> {
  const [items, sections] = await Promise.all([
    db.items.where('roomId').equals(roomId).toArray(),
    db.sections.where('roomId').equals(roomId).toArray(),
  ]);

  const tree = build(items, sections);
  const section = sectionId === undefined ? undefined : tree.sections.get(sectionId);

  return {
    entries: sectionId !== undefined && section === undefined ? [] : tree.entriesIn(sectionId),
    roomItems: items,
    count: tree.countIn(sectionId),
    roomCount: items.length,
    path: tree.pathTo(section),
    section,
  };
}

/** The same tree, for callers that need one number rather than a screen. */
export async function countInSection(sectionId: string): Promise<number> {
  const section = await db.sections.get(sectionId);
  if (section === undefined) return 0;

  const [items, sections] = await Promise.all([
    db.items.where('roomId').equals(section.roomId).toArray(),
    db.sections.where('roomId').equals(section.roomId).toArray(),
  ]);

  return build(items, sections).countIn(sectionId);
}

/** Whether `sectionId` is `maybeAncestor` or sits somewhere inside it. */
export async function isWithin(sectionId: string, maybeAncestor: string): Promise<boolean> {
  let at: string | undefined = sectionId;
  // A malformed parent chain would spin forever; the room is the ceiling.
  for (let hops = 0; at !== undefined && hops < 1000; hops += 1) {
    if (at === maybeAncestor) return true;
    at = (await db.sections.get(at))?.parentId;
  }
  return false;
}

/** Newest first, the same direction a board reads. */
const byNewest = (a: { sort: number }, b: { sort: number }) => b.sort - a.sort;

/** Key for the room's own top level, which has no section id. */
const ROOT = ' root';

function build(items: readonly Item[], sections: readonly Section[]) {
  const byId = new Map(sections.map((section) => [section.id, section]));

  const childItems = new Map<string, Item[]>();
  for (const item of items) push(childItems, item.sectionId ?? ROOT, item);

  const childSections = new Map<string, Section[]>();
  for (const section of sections) push(childSections, section.parentId ?? ROOT, section);

  const counts = new Map<string, number>();
  function countIn(sectionId: string | undefined): number {
    const key = sectionId ?? ROOT;
    const known = counts.get(key);
    if (known !== undefined) return known;

    // Marked before recursing: a parent chain that somehow loops would
    // otherwise take the stack with it.
    counts.set(key, 0);
    const total =
      (childItems.get(key)?.length ?? 0) +
      (childSections.get(key) ?? []).reduce((sum, child) => sum + countIn(child.id), 0);

    counts.set(key, total);
    return total;
  }

  /**
   * The four newest photos inside, section or not — a section of sections
   * still has to show something. Reads in board order, so the cover matches
   * what you see when you open it.
   */
  function coversIn(sectionId: string | undefined, want: number): string[] {
    const found: string[] = [];

    for (const entry of entriesIn(sectionId)) {
      if (found.length >= want) break;

      if (entry.kind === 'item') {
        if (entry.item.coverImageId !== undefined) found.push(entry.item.coverImageId);
      } else {
        const own = entry.section.coverImageId;
        if (own !== undefined) found.push(own);
        else found.push(...coversIn(entry.section.id, want - found.length));
      }
    }

    return found.slice(0, want);
  }

  function entriesIn(sectionId: string | undefined): BoardEntry[] {
    const key = sectionId ?? ROOT;

    const entries: BoardEntry[] = [
      ...(childItems.get(key) ?? []).map(
        (item): BoardEntry => ({ kind: 'item', id: item.id, sort: item.sort, item }),
      ),
      ...(childSections.get(key) ?? []).map(
        (section): BoardEntry => ({
          kind: 'section',
          id: section.id,
          sort: section.sort,
          section,
          count: countIn(section.id),
          covers: [],
        }),
      ),
    ];

    return entries.sort(byNewest);
  }

  function pathTo(section: Section | undefined): Section[] {
    const path: Section[] = [];
    let at = section;
    for (let hops = 0; at !== undefined && hops < 1000; hops += 1) {
      path.unshift(at);
      at = at.parentId === undefined ? undefined : byId.get(at.parentId);
    }
    return path;
  }

  return {
    sections: byId,
    countIn,
    pathTo,
    entriesIn: (sectionId: string | undefined) =>
      // Covers are filled in here rather than in `entriesIn` because that one
      // recurses into itself to find them.
      entriesIn(sectionId).map((entry) =>
        entry.kind === 'section'
          ? { ...entry, covers: coversIn(entry.section.id, COVERS) }
          : entry,
      ),
  };
}

function push<T>(map: Map<string, T[]>, key: string, value: T): void {
  const list = map.get(key);
  if (list === undefined) map.set(key, [value]);
  else list.push(value);
}
